import type { ConfiguracaoOficinaCorte, FerramentaOficinaCorte } from './oficinaCorte'
import type { EstadoTetoLateral } from './rebatimentoLateralTeto'
import type { EstadoElevacaoPainel } from './elevacaoPainel'
import type { EstadoRebatimentoPainel } from './rebatimentoPainel'
import type { PerfilVeiculo } from './perfilVeiculo'
import { zonasDaEstabilizacao } from './estabilizacao'

export type IdApoioExtensor = 'baseSoleira' | 'painel'

export interface EstadoExtensorOficina {
  primeiroApoio: IdApoioExtensor | null
  segundoApoio: IdApoioExtensor | null
  deslocamentoPainel: number
  concluida: boolean
  rebatimento?: EstadoRebatimentoPainel
  elevacao?: EstadoElevacaoPainel
  tetoLateral?: EstadoTetoLateral
}

export interface CondicaoEstabilizacaoExtensor {
  confirmada: boolean
  zonasPrimarias: readonly string[]
  descricao: string
}

export interface ResultadoSelecaoApoio {
  estado: EstadoExtensorOficina
  prontoParaAcionar: boolean
  mensagem: string
}

export const DESLOCAMENTO_MAXIMO_PAINEL = 0.32

export function estadoInicialExtensorOficina(): EstadoExtensorOficina {
  return {
    primeiroApoio: null,
    segundoApoio: null,
    deslocamentoPainel: 0,
    concluida: false,
  }
}

/**
 * A oficina é um modo de demonstração: a posição escolhida já representa um
 * veículo estabilizado. Ainda assim registramos as zonas primárias do mesmo
 * contrato usado no módulo de Estabilização, para não inventar uma segunda
 * definição de apoio para quatro rodas, tombado e capotado.
 */
export function estabilizacaoPreviaDaOficina(
  configuracao: ConfiguracaoOficinaCorte,
  perfil: PerfilVeiculo,
): CondicaoEstabilizacaoExtensor {
  const zonasPrimarias = zonasDaEstabilizacao(configuracao, perfil)
    .filter((zona) => zona.etapa === 'primaria')
    .map((zona) => zona.id)

  return {
    confirmada: zonasPrimarias.length > 0,
    zonasPrimarias,
    descricao: `Estabilização prévia confirmada em ${zonasPrimarias.length} zonas primárias.`,
  }
}

export function apoiosCompativeis(
  primeiro: IdApoioExtensor | null,
  segundo: IdApoioExtensor | null,
): boolean {
  return primeiro !== null
    && segundo !== null
    && primeiro !== segundo
    && new Set([primeiro, segundo]).has('baseSoleira')
    && new Set([primeiro, segundo]).has('painel')
}

export function selecionarApoioExtensor(
  atual: EstadoExtensorOficina,
  apoio: IdApoioExtensor,
  ferramenta: FerramentaOficinaCorte,
  estabilizacao: CondicaoEstabilizacaoExtensor,
): ResultadoSelecaoApoio {
  if (ferramenta !== 'extensor') {
    return { estado: atual, prontoParaAcionar: false, mensagem: 'Equipe o extensor para selecionar seus apoios.' }
  }
  if (!estabilizacao.confirmada) {
    return { estado: atual, prontoParaAcionar: false, mensagem: 'Estabilize o veículo antes de aplicar força estrutural.' }
  }
  if (atual.concluida) {
    return { estado: atual, prontoParaAcionar: false, mensagem: 'O painel já atingiu o limite didático de deslocamento.' }
  }

  if (!atual.primeiroApoio || atual.primeiroApoio === apoio) {
    const estado = { ...atual, primeiroApoio: apoio, segundoApoio: null }
    return {
      estado,
      prontoParaAcionar: false,
      mensagem: apoio === 'baseSoleira'
        ? 'Base apoiada na soleira. Selecione agora o painel.'
        : 'Ponta apoiada no painel. Selecione agora a base estrutural na soleira.',
    }
  }

  const estado = { ...atual, segundoApoio: apoio }
  const prontoParaAcionar = apoiosCompativeis(estado.primeiroApoio, estado.segundoApoio)
  return {
    estado,
    prontoParaAcionar,
    mensagem: prontoParaAcionar
      ? 'Dois apoios compatíveis. Segure para rebater o painel.'
      : 'Os dois apoios precisam ligar a soleira estrutural ao painel móvel.',
  }
}

export function deslocarPainelComExtensor(
  atual: EstadoExtensorOficina,
  progresso: number,
): EstadoExtensorOficina {
  if (!apoiosCompativeis(atual.primeiroApoio, atual.segundoApoio)) return atual
  const limitado = Math.max(0, Math.min(1, progresso))
  return {
    ...atual,
    deslocamentoPainel: Math.max(atual.deslocamentoPainel, limitado * DESLOCAMENTO_MAXIMO_PAINEL),
    concluida: limitado >= 1,
  }
}

export function reiniciarApoiosExtensor(atual: EstadoExtensorOficina): EstadoExtensorOficina {
  return { ...atual, primeiroApoio: null, segundoApoio: null }
}
